import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle, AlertCircle, CreditCard, Calculator, Clock, Shield, Wallet, BanknoteIcon } from 'lucide-react';

interface FAQ {
  question: string;
  answer: string;
  category?: string;
}

interface FAQCategory {
  category: string;
  icon: React.ReactNode;
  questions: FAQ[];
}

const defaultFaqs = [
  {
    category: 'Kredi Başvurusu',
    icon: <CreditCard className="text-[#F7BA45]" size={24} />,
    questions: [
      {
        question: 'Kredi başvurusu için hangi belgeler gereklidir?',
        answer: 'Kredi başvurusu için temel olarak şu belgeler gereklidir:\n\n• Kimlik belgesi (Nüfus cüzdanı, ehliyet veya pasaport)\n• Gelir belgesi (Maaş bordrosu veya serbest çalışanlar için gelir beyanı)\n• İkametgah belgesi\n• Son 3 aylık banka hesap özeti\n\nBazı bankalar ek belgeler talep edebilir. Başvuru öncesi seçtiğiniz bankanın gereksinimlerini kontrol etmenizi öneririz.'
      },
      {
        question: 'Online kredi başvurusu ne kadar sürede sonuçlanır?',
        answer: 'Online kredi başvuruları genellikle çok hızlı değerlendirilir:\n\n• İhtiyaç kredisi: 5-10 dakika\n• Konut kredisi: 24-48 saat\n• Taşıt kredisi: 1-2 saat\n\nBaşvurunuzun hızlı sonuçlanması için tüm belgelerin eksiksiz ve doğru olması önemlidir.'
      }
    ]
  },
  {
    category: 'Kredi Hesaplama',
    icon: <Calculator className="text-[#F7BA45]" size={24} />,
    questions: [
      {
        question: 'Kredi faiz oranları nasıl belirlenir?',
        answer: 'Kredi faiz oranları şu faktörlere göre belirlenir:\n\n• TCMB politika faizi\n• Kredi türü (ihtiyaç, konut, taşıt)\n• Vade süresi\n• Kredi tutarı\n• Müşteri kredi notu\n• Bankanın risk politikaları\n\nKrediPark üzerinden tüm bankaların güncel faiz oranlarını karşılaştırabilirsiniz.'
      },
      {
        question: 'Kredi taksit tutarı nasıl hesaplanır?',
        answer: 'Kredi taksit tutarı şu bileşenleri içerir:\n\n• Ana para\n• Faiz tutarı\n• KKDF (Kaynak Kullanımı Destekleme Fonu)\n• BSMV (Banka Sigorta Muamele Vergisi)\n\nKrediPark\'ın kredi hesaplama aracı ile farklı vade ve tutarlara göre ödeme planınızı kolayca oluşturabilirsiniz.'
      }
    ]
  },
  {
    category: 'Kredi Notu',
    icon: <Shield className="text-[#F7BA45]" size={24} />,
    questions: [
      {
        question: 'Kredi notum düşükse kredi çekebilir miyim?',
        answer: 'Düşük kredi notuna sahip kişiler için çeşitli alternatifler mevcuttur:\n\n• Kefil göstererek kredi başvurusu\n• İpotek karşılığı kredi\n• Düşük kredi notlular için özel kredi paketleri\n• Teminatlı kredi seçenekleri\n\nKrediPark, kredi notunuza uygun en avantajlı seçenekleri sunmaktadır.'
      },
      {
        question: 'Kredi notumu nasıl yükseltebilirim?',
        answer: 'Kredi notunuzu yükseltmek için uygulayabileceğiniz stratejiler:\n\n• Kredi kartı ve kredi ödemelerinizi zamanında yapın\n• Kredi kartı limitinizin %30\'undan fazlasını kullanmayın\n• Gereksiz kredi başvurularından kaçının\n• Düzenli gelir akışınızı koruyun\n• Çeşitli kredi ürünlerini kontrollü kullanın'
      }
    ]
  },
  {
    category: 'Ödemeler ve Vadeler',
    icon: <Clock className="text-[#F7BA45]" size={24} />,
    questions: [
      {
        question: 'Kredi erken kapatma durumunda ceza ödenir mi?',
        answer: 'Kredi erken kapatma işlemlerinde:\n\n• Tüketici kredilerinde yasal sınırlamalar mevcuttur\n• Kalan vadeye göre hesaplanan makul bir erken ödeme ücreti alınabilir\n• Erken kapatma ücreti maksimum oranları yasa ile belirlenmiştir\n• Konut kredilerinde erken ödeme cezası daha düşüktür\n\nErken ödeme yapmadan önce bankanızdan detaylı bilgi almanızı öneririz.'
      },
      {
        question: 'Taksit ödemelerimi nasıl yapabilirim?',
        answer: 'Kredi taksit ödemelerinizi şu kanallardan yapabilirsiniz:\n\n• İnternet bankacılığı\n• Mobil bankacılık\n• ATM\'ler\n• Banka şubeleri\n• Otomatik ödeme talimatı\n\nÖdeme tarihinden önce hesabınızda yeterli bakiye bulundurmanız önemlidir.'
      }
    ]
  },
  {
    category: 'Özel Durumlar',
    icon: <Wallet className="text-[#F7BA45]" size={24} />,
    questions: [
      {
        question: 'Emekliler hangi kredi avantajlarından yararlanabilir?',
        answer: 'Emekliler için özel kredi avantajları:\n\n• Düşük faiz oranları\n• Esnek ödeme seçenekleri\n• Masrafsız kredi imkanları\n• Yüksek kredi limitleri\n• Özel sigorta paketleri\n\nKrediPark üzerinden emeklilere özel kampanyaları inceleyebilirsiniz.'
      },
      {
        question: 'Yapılandırma veya refinansman kredisi nedir?',
        answer: 'Yapılandırma kredisi mevcut kredilerinizi tek bir kredide toplama imkanı sunar:\n\n• Daha düşük faiz oranı\n• Tek ve düzenli ödeme planı\n• Uzun vade seçeneği\n• Aylık taksit tutarında azalma\n• Borç yönetiminde kolaylık\n\nMevcut kredi borçlarınızı yapılandırmak için bankaların özel tekliflerini karşılaştırabilirsiniz.'
      }
    ]
  }
];

interface FAQProps {
  customFaqs?: FAQ[];
}

const FAQ: React.FC<FAQProps> = ({ customFaqs }) => {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  // Process custom FAQs if provided
  const processedFaqs: FAQCategory[] = customFaqs 
    ? processFaqs(customFaqs)
    : defaultFaqs;

  // Function to process custom FAQs into categories
  function processFaqs(faqs: FAQ[]): FAQCategory[] {
    const categoriesMap: Record<string, FAQ[]> = {};
    
    // Group FAQs by category
    faqs.forEach(faq => {
      const category = faq.category || 'Genel';
      if (!categoriesMap[category]) {
        categoriesMap[category] = [];
      }
      categoriesMap[category].push(faq);
    });
    
    // Convert to array of categories with icons
    return Object.entries(categoriesMap).map(([category, questions]) => {
      let icon;
      switch (category) {
        case 'Kredi Başvurusu':
          icon = <CreditCard className="text-[#F7BA45]" size={24} />;
          break;
        case 'Kredi Hesaplama':
          icon = <Calculator className="text-[#F7BA45]" size={24} />;
          break;
        case 'Kredi Notu':
          icon = <Shield className="text-[#F7BA45]" size={24} />;
          break;
        case 'Ödemeler ve Vadeler':
          icon = <Clock className="text-[#F7BA45]" size={24} />;
          break;
        case 'Özel Durumlar':
          icon = <Wallet className="text-[#F7BA45]" size={24} />;
          break;
        default:
          icon = <BanknoteIcon className="text-[#F7BA45]" size={24} />;
      }
      
      return {
        category,
        icon,
        questions
      };
    });
  }

  // Split faqs into two columns
  const midpoint = Math.ceil(processedFaqs.length / 2);
  const leftColumnFaqs = processedFaqs.slice(0, midpoint);
  const rightColumnFaqs = processedFaqs.slice(midpoint);

  const renderFAQColumn = (columnFaqs: FAQCategory[]) => (
    <div className="space-y-6">
      {columnFaqs.map((category) => (
        <div 
          key={category.category}
          className={`bg-white rounded-xl overflow-hidden border transition-all duration-300
            ${openCategory === category.category 
              ? 'border-[#F7BA45] shadow-md' 
              : 'border-slate-200'
            }`}
        >
          <button
            className="w-full px-6 py-4 text-left flex items-center justify-between bg-slate-50"
            onClick={() => setOpenCategory(openCategory === category.category ? null : category.category)}
          >
            <div className="flex items-center gap-3">
              {category.icon}
              <span className="font-semibold text-slate-800">{category.category}</span>
            </div>
            <ChevronDown 
              size={20} 
              className={`text-slate-500 transition-transform duration-300 ${
                openCategory === category.category ? 'transform rotate-180 text-[#F7BA45]' : ''
              }`}
            />
          </button>
          
          <div 
            className={`overflow-hidden transition-all duration-300 ${
              openCategory === category.category ? 'max-h-[1000px]' : 'max-h-0'
            }`}
          >
            <div className="p-4 space-y-4">
              {category.questions.map((faq) => (
                <div 
                  key={faq.question}
                  className={`rounded-lg border transition-all duration-300 ${
                    openQuestion === faq.question
                      ? 'border-[#F7BA45]/50 bg-[#F7BA45]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <button
                    className="w-full px-4 py-3 text-left flex items-center justify-between"
                    onClick={() => setOpenQuestion(openQuestion === faq.question ? null : faq.question)}
                  >
                    <div className="flex items-center gap-3">
                      {openQuestion === faq.question ? (
                        <AlertCircle size={20} className="text-[#F7BA45]" />
                      ) : (
                        <HelpCircle size={20} className="text-slate-400" />
                      )}
                      <span className="font-medium text-slate-800">{faq.question}</span>
                    </div>
                    <ChevronDown 
                      size={20} 
                      className={`text-slate-500 transition-transform duration-300 ${
                        openQuestion === faq.question ? 'transform rotate-180 text-[#F7BA45]' : ''
                      }`}
                    />
                  </button>
                  
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${
                      openQuestion === faq.question ? 'max-h-[500px]' : 'max-h-0'
                    }`}
                  >
                    <div className="px-4 pb-4 text-slate-600 whitespace-pre-line">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {renderFAQColumn(leftColumnFaqs)}
      {renderFAQColumn(rightColumnFaqs)}
    </div>
  );
};

export default FAQ;